/**
 * Work note changes, kept out of the panel so they can be tested (Sun 18, Josh 11 Oct 2026).
 *
 * A cue keeps its work notes in data.workNote: an array of {note, archived, editedAt}
 * with at most one open (archived === false). Old projects can still hold a plain
 * string there, which the panel used to crash on when archiving or exporting.
 */

// A cue's work note, or '' when the cue has no workNote field at all (cues from 2019-20; 20 Oct 2026).
export function workNoteOf(cue) {
    const w = cue && cue.data ? cue.data.workNote : undefined;
    return w == null ? '' : w;
}

const copy = workNote => (Array.isArray(workNote) ? workNote.map(n => ({ ...n })) : []);

// Archive the note with this editedAt. A plain-string note becomes one archived entry.
export function archiveNote(workNote, editedAt, fallbackEditedAt) {
    if (typeof workNote === 'string') {
        return [{ archived: true, note: workNote, editedAt: fallbackEditedAt }];
    }
    return copy(workNote).map(n => (n.editedAt === editedAt ? { ...n, archived: true } : n));
}

// Reopen an archived note. The cue's current open note is ARCHIVED, never deleted
// (before 11 Oct it was spliced out and lost).
export function restoreNote(workNote, editedAt) {
    if (!Array.isArray(workNote)) return workNote; // nothing archived to restore; never wipe a string note
    return copy(workNote).map((n) => {
        if (n.editedAt === editedAt) return { ...n, archived: false };
        if (n.archived === false) return { ...n, archived: true };
        return n;
    });
}

// Delete one archived note for good.
export function removeNote(workNote, editedAt) {
    if (!Array.isArray(workNote)) return workNote;
    return copy(workNote).filter(n => n.editedAt !== editedAt);
}

// The cue's note box after a blur (29 Oct 2026). Returns the new data.workNote, or null when nothing changed:
// the text equals the open note, or the box is empty and there is no open note. Before this, every blur rewrote
// the open note with a new editedAt (stamping the show edited) and an empty blur made a blank open note.
export function writeOpenNote(workNote, text, now) {
    const value = text == null ? '' : String(text);
    let open = null;
    if (typeof workNote === 'string' && workNote !== '') open = workNote;
    if (Array.isArray(workNote)) open = (workNote.find(n => n.archived === false) || {}).note;
    const hasOpen = open != null;
    if (hasOpen && value === open) return null;
    if (!hasOpen && value === '') return null;
    const kept = Array.isArray(workNote) ? copy(workNote).filter(n => n.archived !== false) : [];
    return [...kept, { note: value, archived: false, editedAt: now }];
}

export function openNoteText(workNote) {
    if (typeof workNote === 'string') return workNote;
    if (!Array.isArray(workNote)) return '';
    return workNote.filter(n => !n.archived).map(n => n.note).join('; ');
}

const field = v => '"' + String(v == null ? '' : v).replaceAll('"', '""') + '"';

export function workNotesCsv(rows) {
    let csv = ['Page Number', 'Layer Title', 'Cue Number', 'Work Note'].join(',') + '\n';
    for (const r of rows) {
        csv += [field(r.page), field(r.layer), field(r.cue), field(r.note)].join(',') + '\n';
    }
    return csv;
}


// WEBPACK FOOTER //
// ./src/lib/worknotes.js